// Department (tenant) VIEW scope for the admin console.
//
// A super_admin / reseller can pick a department from the top bar. The active
// selection is stored in localStorage and injected as `?domain_uuid=<uuid>` on
// API GETs by lib/api.ts, so the existing pages render scoped to that one
// department without any per-page changes. `all` (or unset) = no filter.
//
// IMPORTANT: this is a VIEW filter for the admin only. It does NOT enforce
// tenant isolation on the server — a real per-tenant login is still not
// confined by this. See SECURITY-AUDIT.md (#1) for the separate enforcement
// work, which is intentionally out of scope here.

import { useEffect, useState } from 'react';
import { ALL, resolveScopedDomain } from './scopedDomain';

export { ALL };

const KEY = 'stratus_active_tenant';

export function getActiveTenant(): string {
  try { return localStorage.getItem(KEY) || ALL; } catch { return ALL; }
}

// True if a row belonging to `domainUuid` should be shown under the current
// department filter. "All" shows everything. Used by the operator-console pages
// (Call Handling, Voice & Chat, Departments) that aggregate across tenants and
// must filter client-side, since they fan out over /api/tenants (not scoped).
export function matchesScope(domainUuid?: string | null): boolean {
  const a = getActiveTenant();
  return a === ALL || domainUuid === a;
}

// The domain a page should fetch for. Pages historically hardcoded the admin's
// own JWT tenant (me.domain_uuid), so a super_admin only ever saw their home
// department. This prefers the globally-selected department when one is set,
// falling back to the page's own default ("All" => fallback, unchanged).
// Pure core lives in scopedDomain.ts so it's testable under node:test (#942).
export function getScopedDomain(fallback?: string | null): string | undefined {
  return resolveScopedDomain(getActiveTenant(), fallback);
}

export function setActiveTenant(v: string) {
  try {
    if (!v || v === ALL) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, v);
  } catch {}
  try {
    window.dispatchEvent(new CustomEvent('stratus-tenant-changed', { detail: getActiveTenant() }));
  } catch {}
}

// React-reactive read of the active tenant. A bare getActiveTenant() in a
// component is a render-time snapshot — after setActiveTenant the component
// never re-renders, so a controlled <select value=...> snaps back to the stale
// value (the webclient switcher bug). Subscribes to the change event (and
// cross-tab storage events) so consumers re-render on every switch.
export function useActiveTenant(): string {
  const [v, setV] = useState(getActiveTenant());
  useEffect(() => {
    const on = () => setV(getActiveTenant());
    window.addEventListener('stratus-tenant-changed', on);
    window.addEventListener('storage', on);
    return () => {
      window.removeEventListener('stratus-tenant-changed', on);
      window.removeEventListener('storage', on);
    };
  }, []);
  return v;
}

// Paths that must NEVER be scoped: auth, the department list itself (the
// dropdown's own data source), the logged-in user, and global/system-wide
// catalogs. Everything else under /api/ is eligible.
const EXCLUDE = [
  '/api/auth',
  '/api/tenants',
  '/api/me',
  '/api/defs',
  '/api/parameters',
  '/api/license',
  '/api/reseller',
  '/api/billing',               // operator billing is cross-tenant; the page passes explicit ?domain_uuid= per row (#1050)
  '/api/system/parking-pool',   // global spot pool (owns the per-department assignment) — not per-tenant
];

export function shouldScope(path: string): boolean {
  if (!path.startsWith('/api/')) return false;
  return !EXCLUDE.some(p => path === p || path.startsWith(p + '/') || path.startsWith(p + '?'));
}
